import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, ChevronDown, UserRound } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [isJamaatOpen, setIsJamaatOpen] = useState(false);
  const [isResourcesOpen, setIsResourcesOpen] = useState(false);
  // Name of the signed-in person (from the auth worker), shown top right. Hidden when signed out.
  const { isAuthenticated, user } = useAuth();
  const userName = isAuthenticated ? user?.name : null;

  const toggleMenu = () => setIsOpen(!isOpen);

  const jamaatLinks = [
    { name: 'Anjuman Committee', path: '/jamaat/anjuman' },
    { name: 'Advisory Committee', path: '/jamaat/advisory' },
    { name: '12 Umoor Committees', path: '/jamaat/12umoor' },
  ];

  const resourcesLinks = [
    { name: 'Jamaat Office', path: '/resources/jamaat-office' },
    { name: 'Calendar', path: '/resources/calendar' },
    { name: 'Directory', path: '/resources/directory' },
    { name: 'Ashara 1448', path: '/resources/ashara1448relaycenter' },
    { name: 'Qardan Hasanah', path: '/resources/qardan-hasanah' },
    { name: 'Business Listings', path: '/resources/business-listings' },
    { name: 'Sehat Newsletters', path: '/resources/UmoorSehat' },
    { name: 'Qabrastan', path: '/resources/cemetery' },
    { name: 'Ziyarat', path: '/resources/ziyarat' },
  ];

  const portalLinks = [
    { name: 'FMB Portal', path: 'https://fmb.njjamaat.org/' },
    { name: 'Finance Portal', path: 'https://finance.njjamaat.org/' },
  ];

  return (
    <nav className="sticky top-0 z-50 bg-white shadow-md">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3 group">
            <img
              src="/logo.png"
              alt="Dawoodi Bohra Logo"
              className="h-12 w-auto object-contain group-hover:opacity-80 transition-opacity"
            />

            <span className="hidden sm:inline font-playfair text-2xl font-bold text-dawoodi-navy">
              Anjuman-e-Burhani NJ
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-1">
            <Link
              to="/"
              className="px-4 py-2 text-gray-700 hover:text-dawoodi-gold font-poppins font-medium transition-colors duration-200 relative group"
            >
              Home
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-dawoodi-gold to-dawoodi-gold group-hover:w-full transition-all duration-300"></span>
            </Link>

            {/* Jamaat Dropdown */}
            <div className="relative group">
              <button className="px-4 py-2 text-gray-700 hover:text-dawoodi-gold font-poppins font-medium transition-colors duration-200 relative flex items-center space-x-1">
                <span>Jamaat</span>
                <ChevronDown size={16} />
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-dawoodi-gold to-dawoodi-gold group-hover:w-full transition-all duration-300"></span>
              </button>

              <div className="absolute left-0 top-full pt-2 hidden group-hover:block">
                <div className="w-64 bg-white shadow-lg rounded-xl border border-dawoodi-gold/20 py-2">
                  {jamaatLinks.map((link) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      className="block px-4 py-3 text-gray-700 hover:bg-dawoodi-light hover:text-dawoodi-gold font-poppins font-medium transition-all duration-200"
                    >
                      {link.name}
                    </Link>
                  ))}
                </div>
              </div>
            </div>

            {/* Resources Dropdown */}
            <div className="relative group">
              <button className="px-4 py-2 text-gray-700 hover:text-dawoodi-gold font-poppins font-medium transition-colors duration-200 relative flex items-center space-x-1">
                <span>Resources</span>
                <ChevronDown size={16} />
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-dawoodi-gold to-dawoodi-gold group-hover:w-full transition-all duration-300"></span>
              </button>

              <div className="absolute left-0 top-full pt-2 hidden group-hover:block">
                <div className="w-72 bg-white shadow-lg rounded-xl border border-dawoodi-gold/20 py-2">
                  {resourcesLinks.map((link) =>
                    link.external ? (
                      <a
                        key={link.path}
                        href={link.path}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block px-4 py-3 text-gray-700 hover:bg-dawoodi-light hover:text-dawoodi-gold font-poppins font-medium transition-all duration-200"
                      >
                        {link.name}
                      </a>
                    ) : (
                      <Link
                        key={link.path}
                        to={link.path}
                        className="block px-4 py-3 text-gray-700 hover:bg-dawoodi-light hover:text-dawoodi-gold font-poppins font-medium transition-all duration-200"
                      >
                        {link.name}
                      </Link>
                    )
                  )}
                </div>
              </div>
            </div>

            {portalLinks.map((link) => (
              <a
                key={link.path}
                href={link.path}
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 py-2 text-gray-700 hover:text-dawoodi-gold font-poppins font-medium transition-colors duration-200 relative group"
              >
                {link.name}
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-dawoodi-gold to-dawoodi-gold group-hover:w-full transition-all duration-300"></span>
              </a>
            ))}

            {/* Signed-in user, mid-size screens: at the end of the menu (no room beside it) */}
            {userName && (
              <div
                className="ml-3 pl-4 border-l border-gray-200 flex min-[1720px]:hidden items-center space-x-2 text-dawoodi-navy font-poppins font-medium"
                title={`Signed in as ${userName}`}
              >
                <UserRound size={18} className="text-dawoodi-gold shrink-0" />
                <span className="max-w-[12rem] text-sm leading-snug line-clamp-2 break-words">{userName}</span>
              </div>
            )}
          </div>

          {/* Mobile: signed-in user's name next to the menu button */}
          {userName && (
            <div className="md:hidden ml-auto mr-2 flex items-center space-x-1.5 text-dawoodi-navy font-poppins text-sm font-medium" title={`Signed in as ${userName}`}>
              <UserRound size={16} className="text-dawoodi-gold shrink-0" />
              <span className="max-w-[13rem] leading-snug line-clamp-2 break-words">{userName}</span>
            </div>
          )}

          {/* Mobile menu button */}
          <button
            onClick={toggleMenu}
            className="md:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
            aria-label="Toggle menu"
          >
            {isOpen ? (
              <X size={24} className="text-dawoodi-navy" />
            ) : (
              <Menu size={24} className="text-dawoodi-navy" />
            )}
          </button>
        </div>

        {/* Signed-in user, wide screens (1720px+): pinned to the header's top-right corner,
            in the space beside the centered menu. Long names (titles included) wrap onto
            two lines; only beyond that are they cut off with "...", and the full name shows on hover. */}
        {userName && (
          <div
            className="hidden min-[1720px]:flex absolute right-6 top-0 h-20 items-center space-x-2 text-dawoodi-navy font-poppins font-medium"
            title={`Signed in as ${userName}`}
          >
            <UserRound size={18} className="text-dawoodi-gold shrink-0" />
            <span className="max-w-[12rem] text-sm leading-snug line-clamp-2 break-words">{userName}</span>
          </div>
        )}

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="md:hidden pb-4 space-y-2 animate-in fade-in slide-in-from-top-2">
            <Link
              to="/"
              className="block px-4 py-3 text-gray-700 hover:bg-dawoodi-light hover:text-dawoodi-gold font-poppins font-medium transition-all duration-200 rounded-lg"
              onClick={() => setIsOpen(false)}
            >
              Home
            </Link>

            {/* Mobile Jamaat Dropdown */}
            <button
              onClick={() => setIsJamaatOpen(!isJamaatOpen)}
              className="w-full flex items-center justify-between px-4 py-3 text-gray-700 hover:bg-dawoodi-light hover:text-dawoodi-gold font-poppins font-medium transition-all duration-200 rounded-lg"
            >
              <span>Jamaat</span>
              <ChevronDown
                size={18}
                className={`transition-transform duration-200 ${
                  isJamaatOpen ? 'rotate-180' : ''
                }`}
              />
            </button>

            {isJamaatOpen && (
              <div className="ml-4 space-y-1">
                {jamaatLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    className="block px-4 py-2 text-gray-600 hover:bg-dawoodi-light hover:text-dawoodi-gold font-poppins font-medium transition-all duration-200 rounded-lg"
                    onClick={() => {
                      setIsOpen(false);
                      setIsJamaatOpen(false);
                    }}
                  >
                    {link.name}
                  </Link>
                ))}
              </div>
            )}

            {/* Mobile Resources Dropdown */}
            <button
              onClick={() => setIsResourcesOpen(!isResourcesOpen)}
              className="w-full flex items-center justify-between px-4 py-3 text-gray-700 hover:bg-dawoodi-light hover:text-dawoodi-gold font-poppins font-medium transition-all duration-200 rounded-lg"
            >
              <span>Resources</span>
              <ChevronDown
                size={18}
                className={`transition-transform duration-200 ${
                  isResourcesOpen ? 'rotate-180' : ''
                }`}
              />
            </button>

            {isResourcesOpen && (
              <div className="ml-4 space-y-1">
                {resourcesLinks.map((link) =>
                  link.external ? (
                    <a
                      key={link.path}
                      href={link.path}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block px-4 py-2 text-gray-600 hover:bg-dawoodi-light hover:text-dawoodi-gold font-poppins font-medium transition-all duration-200 rounded-lg"
                      onClick={() => {
                        setIsOpen(false);
                        setIsResourcesOpen(false);
                      }}
                    >
                      {link.name}
                    </a>
                  ) : (
                    <Link
                      key={link.path}
                      to={link.path}
                      className="block px-4 py-2 text-gray-600 hover:bg-dawoodi-light hover:text-dawoodi-gold font-poppins font-medium transition-all duration-200 rounded-lg"
                      onClick={() => {
                        setIsOpen(false);
                        setIsResourcesOpen(false);
                      }}
                    >
                      {link.name}
                    </Link>
                  )
                )}
              </div>
            )}

            {portalLinks.map((link) => (
              <a
                key={link.path}
                href={link.path}
                target="_blank"
                rel="noopener noreferrer"
                className="block px-4 py-3 text-gray-700 hover:bg-dawoodi-light hover:text-dawoodi-gold font-poppins font-medium transition-all duration-200 rounded-lg"
                onClick={() => setIsOpen(false)}
              >
                {link.name}
              </a>
            ))}
          </div>
        )}
      </div>
    </nav>
  );
}
